/**
 * TMUsersPage — admin-only: approve/reject members, set per-user permissions,
 * send approval notifications via WhatsApp and email.
 */
import { useEffect, useState } from 'react';
import {
  CheckCircle, XCircle, ShieldCheck, ShieldOff,
  ChevronDown, MessageCircle, Mail, Settings,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTMAuth } from '@/contexts/TMAuthContext';
import {
  getAllTMUsers, approveTMUser, rejectTMUser, deactivateTMUser,
  updateTMUserRole, updateTMUserPermissions,
  buildWhatsAppUrl, userApprovedWhatsAppMessage,
} from '@/services/tmService';
import type { TMUser, TMUserStatus, TMUserRole, TMUserPermissions } from '@/types/tm.types';
import { DEFAULT_TM_PERMISSIONS } from '@/types/tm.types';
import { cn } from '@/utils/cn';

const APP_URL = 'https://www.wombcat.com/tm';

const STATUS_BADGE: Record<TMUserStatus, string> = {
  pending:     'bg-amber-100 text-amber-700',
  approved:    'bg-emerald-100 text-emerald-700',
  rejected:    'bg-red-100 text-red-600',
  deactivated: 'bg-gray-100 text-gray-500',
};
const STATUS_LABEL: Record<TMUserStatus, string> = {
  pending:     'Pending',
  approved:    'Active',
  rejected:    'Rejected',
  deactivated: 'Deactivated',
};

const PERM_LABELS: { key: keyof TMUserPermissions; label: string }[] = [
  { key: 'createTask',   label: 'Create tasks' },
  { key: 'editTask',     label: 'Edit tasks' },
  { key: 'updateTask',   label: 'Update status/progress' },
  { key: 'assignTask',   label: 'Assign/reassign tasks' },
  { key: 'approveUsers', label: 'Approve new members' },
];

type Tab = 'pending' | 'approved' | 'all';

function PermissionPanel({ user, onSave }: { user: TMUser; onSave: (uid: string, p: TMUserPermissions) => void }) {
  const [perms, setPerms] = useState<TMUserPermissions>(user.permissions ?? DEFAULT_TM_PERMISSIONS);
  const [dirty, setDirty] = useState(false);

  const toggle = (key: keyof TMUserPermissions) => {
    setPerms(p => ({ ...p, [key]: !p[key] }));
    setDirty(true);
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Permissions</p>
      <div className="space-y-2">
        {PERM_LABELS.map(({ key, label }) => (
          <label key={key} className="flex items-center gap-3 cursor-pointer">
            <div
              onClick={() => toggle(key)}
              className={cn(
                'w-9 h-5 rounded-full relative transition-colors cursor-pointer',
                perms[key] ? 'bg-indigo-500' : 'bg-gray-200',
              )}>
              <div className={cn(
                'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
                perms[key] ? 'translate-x-4' : 'translate-x-0.5',
              )} />
            </div>
            <span className="text-xs text-gray-700">{label}</span>
          </label>
        ))}
      </div>
      {dirty && (
        <button
          onClick={() => { onSave(user.uid, perms); setDirty(false); }}
          className="mt-3 w-full py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold active:scale-95 transition-all">
          Save Permissions
        </button>
      )}
    </div>
  );
}

export default function TMUsersPage() {
  const { tmUser } = useTMAuth();
  const [users,    setUsers]    = useState<TMUser[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState<Tab>('pending');
  const [busy,     setBusy]     = useState<string | null>(null);
  const [openPerms, setOpenPerms] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setUsers(await getAllTMUsers());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = users.filter(u =>
    tab === 'pending'  ? u.status === 'pending' :
    tab === 'approved' ? u.status === 'approved' : true
  );
  const pendingCount = users.filter(u => u.status === 'pending').length;

  const handleApprove = async (u: TMUser, role: TMUserRole = 'member') => {
    if (!tmUser) return;
    setBusy(u.uid);
    await approveTMUser(u.uid, tmUser.uid, role);
    toast.success(`${u.name} approved as ${role}`);
    await load();
    setBusy(null);
  };

  const handleReject = async (u: TMUser) => {
    if (!confirm(`Reject ${u.name}?`)) return;
    setBusy(u.uid);
    await rejectTMUser(u.uid);
    toast.success(`${u.name} rejected`);
    await load();
    setBusy(null);
  };

  const handleDeactivate = async (u: TMUser) => {
    if (!confirm(`Deactivate ${u.name}?`)) return;
    setBusy(u.uid);
    await deactivateTMUser(u.uid);
    toast.success(`${u.name} deactivated`);
    await load();
    setBusy(null);
  };

  const handleRoleChange = async (u: TMUser, role: TMUserRole) => {
    setBusy(u.uid);
    await updateTMUserRole(u.uid, role);
    toast.success(`${u.name} is now ${role}`);
    await load();
    setBusy(null);
  };

  const handleSavePermissions = async (uid: string, perms: TMUserPermissions) => {
    await updateTMUserPermissions(uid, perms);
    toast.success('Permissions saved');
    await load();
  };

  const openApprovalWA = (u: TMUser) => {
    if (!u.phone) { toast.error('No phone number on file'); return; }
    const url = buildWhatsAppUrl(u.phone, userApprovedWhatsAppMessage(u.name, APP_URL));
    window.open(url, '_blank');
  };

  const sendApprovalEmail = async (u: TMUser) => {
    try {
      await fetch(
        'https://us-central1-wombcat-cms.cloudfunctions.net/sendUserApprovedNotification',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: u.uid, email: u.email, name: u.name }),
        }
      );
      toast.success(`Approval email sent to ${u.name}`);
    } catch {
      toast.error('Failed to send email');
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-gray-900">Members</h1>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
        {([
          { key: 'pending',  label: 'Pending' },
          { key: 'approved', label: 'Active' },
          { key: 'all',      label: 'All' },
        ] as { key: Tab; label: string }[]).map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            className={cn('relative flex-1 py-2 rounded-lg text-xs font-semibold transition-all',
              tab === key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500')}>
            {label}
            {key === 'pending' && pendingCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 rounded-2xl bg-gray-100 animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-14 text-gray-400 text-sm bg-white rounded-2xl border border-gray-100">
          No {tab === 'pending' ? 'pending' : tab === 'approved' ? 'active' : ''} members.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(u => (
            <div key={u.uid}
              className={cn('bg-white rounded-2xl border border-gray-100 shadow-sm p-4', busy === u.uid && 'opacity-60')}>

              {/* Info row */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-indigo-600">{u.name.charAt(0).toUpperCase()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{u.name}</p>
                  <p className="text-xs text-gray-400 truncate">{u.email}</p>
                  {u.phone && <p className="text-xs text-gray-400">{u.phone}</p>}
                </div>
                <span className={cn('shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full', STATUS_BADGE[u.status])}>
                  {STATUS_LABEL[u.status]}
                </span>
              </div>

              {/* Role + joined */}
              <div className="flex items-center gap-2 text-xs text-gray-400 mt-2">
                <span className={cn('capitalize px-2 py-0.5 rounded-full font-medium',
                  u.role === 'admin' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500')}>
                  {u.role}
                </span>
                {u.createdAt && (
                  <span>Joined {u.createdAt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-2 flex-wrap mt-3">
                {u.status === 'pending' && (
                  <>
                    <button onClick={() => handleApprove(u, 'member')} disabled={!!busy}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
                      <CheckCircle className="h-3.5 w-3.5" /> Approve as Member
                    </button>
                    <button onClick={() => handleApprove(u, 'admin')} disabled={!!busy}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-600 text-white text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
                      <ShieldCheck className="h-3.5 w-3.5" /> Approve as Admin
                    </button>
                    <button onClick={() => handleReject(u)} disabled={!!busy}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-200 text-red-500 text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
                      <XCircle className="h-3.5 w-3.5" /> Reject
                    </button>
                  </>
                )}

                {u.status === 'approved' && (
                  <>
                    <div className="relative">
                      <select value={u.role} disabled={!!busy}
                        onChange={e => handleRoleChange(u, e.target.value as TMUserRole)}
                        className="appearance-none pl-3 pr-7 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 cursor-pointer">
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                    </div>
                    <button onClick={() => setOpenPerms(openPerms === u.uid ? null : u.uid)}
                      className={cn('flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all',
                        openPerms === u.uid ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'border-gray-200 text-gray-500')}>
                      <Settings className="h-3.5 w-3.5" /> Permissions
                    </button>
                    <button onClick={() => handleDeactivate(u)} disabled={!!busy}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-gray-500 text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
                      <ShieldOff className="h-3.5 w-3.5" /> Deactivate
                    </button>
                  </>
                )}

                {(u.status === 'rejected' || u.status === 'deactivated') && (
                  <button onClick={() => handleApprove(u, 'member')} disabled={!!busy}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold active:scale-95 transition-all disabled:opacity-60">
                    <CheckCircle className="h-3.5 w-3.5" /> Re-approve
                  </button>
                )}

                {/* Notification buttons — shown after approval or for approved members */}
                {(u.status === 'approved') && (
                  <>
                    <button onClick={() => openApprovalWA(u)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-white text-xs font-semibold active:scale-95 transition-all">
                      <MessageCircle className="h-3.5 w-3.5" /> Notify (WA)
                    </button>
                    <button onClick={() => sendApprovalEmail(u)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 text-white text-xs font-semibold active:scale-95 transition-all">
                      <Mail className="h-3.5 w-3.5" /> Notify (Email)
                    </button>
                  </>
                )}
              </div>

              {/* Permissions panel */}
              {openPerms === u.uid && u.status === 'approved' && (
                <PermissionPanel user={u} onSave={handleSavePermissions} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
